import { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { fetchWithAuth } from "../api/fetchWithAuth";

const initialForm = {
  name: "",
  currentPassword: "",
  newPassword: "",
  confirmPassword: ""
};

function validateStrongPassword(password) {
  if (password.length < 8) {
    return "New password must be at least 8 characters.";
  }

  if (!/[A-Za-z]/.test(password)) {
    return "New password must contain at least one letter.";
  }

  if (!/\d/.test(password)) {
    return "New password must contain at least one digit.";
  }

  if (!/[^A-Za-z0-9]/.test(password)) {
    return "New password must contain at least one special character.";
  }

  return "";
}

export default function ChangePasswordModal({ show, force, user, onClose, onChanged, onLogout }) {
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const canChangeUsername = user && !user.usernameChanged;

  useEffect(() => {
    if (show) {
      setForm({
        ...initialForm,
        name: user?.name ?? ""
      });
      setError("");
      setSaving(false);
    }
  }, [show, user]);

  const updateField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value
    }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");

    const passwordError = validateStrongPassword(form.newPassword);

    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (form.newPassword !== form.confirmPassword) {
      setError("New password confirmation does not match.");
      return;
    }

    if (canChangeUsername && !form.name.trim()) {
      setError("Username is required.");
      return;
    }

    setSaving(true);

    try {
      const res = await fetchWithAuth("/api/auth/change-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: canChangeUsername ? form.name.trim() : undefined,
          currentPassword: form.currentPassword,
          newPassword: form.newPassword
        })
      });

      if (!res.ok) {
        const responseError = await res.json().catch(() => null);
        throw new Error(responseError?.message || "Password change failed");
      }

      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={force ? undefined : onClose}
      backdrop={force ? "static" : true}
      keyboard={!force}
      centered
    >
      <Form onSubmit={submit}>
        <Modal.Header closeButton={!force}>
          <Modal.Title>
            {force ? "Change temporary password" : "Change password"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {force && (
            <div className="alert alert-warning py-2">
              You must change your temporary password before continuing.
            </div>
          )}

          {error && (
            <div className="alert alert-danger py-2">{error}</div>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Username</Form.Label>
            <Form.Control
              type="text"
              autoComplete="username"
              value={form.name}
              onChange={(event) => updateField("name", event.target.value)}
              disabled={!canChangeUsername}
              required={canChangeUsername}
            />
            <Form.Text muted>
              {canChangeUsername
                ? "You can change your username once."
                : "Username change has already been used."}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Current password</Form.Label>
            <Form.Control
              type="password"
              autoComplete="current-password"
              value={form.currentPassword}
              onChange={(event) => updateField("currentPassword", event.target.value)}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>New password</Form.Label>
            <Form.Control
              type="password"
              autoComplete="new-password"
              value={form.newPassword}
              onChange={(event) => updateField("newPassword", event.target.value)}
              required
            />
            <Form.Text muted>
              Minimum 8 characters, including a letter, a digit and a special character.
            </Form.Text>
          </Form.Group>

          <Form.Group>
            <Form.Label>Confirm new password</Form.Label>
            <Form.Control
              type="password"
              autoComplete="new-password"
              value={form.confirmPassword}
              onChange={(event) => updateField("confirmPassword", event.target.value)}
              required
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          {force ? (
            <Button variant="outline-secondary" onClick={onLogout} disabled={saving}>
              Log out
            </Button>
          ) : (
            <Button variant="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
          )}
          <Button variant="success" type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save password"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
